import { useQuery } from '@tanstack/react-query'
import type { DocumentStatus } from '@/features/documents/api'
import type { ListMeta } from '@/lib/api'
import { useApi } from '@/lib/api-context'

/**
 * Painel root: monitoramento (docs/api/admin.md, "Painel root: monitoramento"; RN-ADM-94 a 99, T61). O que
 * a Westhetics faz sozinha por todas as clínicas, só leitura. As três rotas são de root, com ou sem clínica
 * ativa, e a tela as pede de novo a cada minuto. Os nomes de campo são os de
 * `apps/api/supabase/functions/admin/operations/schemas.ts`.
 */

export type OperationsPeriod = 'day' | 'week'
export const OPERATIONS_PERIODS: readonly OperationsPeriod[] = ['day', 'week']

export type OperationArea =
  'jobs' | 'sends' | 'autentique' | 'pdf' | 'email' | 'integrations' | 'api'
export type OperationAreaFilter = 'all' | OperationArea
export const OPERATION_AREA_FILTERS: readonly OperationAreaFilter[] = [
  'all',
  'jobs',
  'sends',
  'autentique',
  'pdf',
  'email',
  'integrations',
  'api',
]

export type JobName = 'signatures' | 'photos' | 'integrations'
/** RN-ADM-96: em dia, atrasada (mais de 5 minutos sem rodar), falhou, nunca rodou. */
export type JobStatus = 'on_time' | 'late' | 'failed' | 'never'

export interface JobHeartbeat {
  job: JobName
  status: JobStatus
  lastRunAt: string | null
  lastOkAt: string | null
  lastFailedAt: string | null
  /** A causa da última rodada que deu errado ("a chave do agendador não confere"). */
  lastError: string | null
}

/** Uma linha de "O que pede atenção" (RN-ADM-97), já com a frase da tela. */
export interface AttentionItem {
  level: 'critical' | 'attention'
  area: OperationArea
  code: string
  text: string
}

export interface OperationsOverview {
  generatedAt: string
  period: OperationsPeriod
  since: string
  attention: AttentionItem[]
  /** Sempre as três, nesta ordem: assinaturas, fotos, integrações. */
  jobs: JobHeartbeat[]
  sends: {
    sent: number
    failed: number
    queued: number
    manual: number
    failedByChannel: { whatsapp: number; email: number }
  }
  autentique: {
    isConfigured: boolean
    isSandbox: boolean
    sent: number
    queued: number
    retrying: number
    failed: number
    lastFailureDetail: string | null
    events: {
      received: number
      pending: number
      retrying: number
      exhausted: number
      rejected: number
    }
  }
  pdf: { isConfigured: boolean; failures: number; rateLimited: number; generatedDocuments: number }
  email: { mode: 'resend' | 'console'; delivered: number; failures: number }
  integrations: {
    delivered: number
    failed: number
    retrying: number
    disabledWebhooks: number
    requests: number
  }
  database: {
    connections: number
    maxConnections: number
    sizeBytes: number
    storageObjects: number
    storageBytes: number
  }
  apiErrors: number
}

export interface OperationClinic {
  id: string
  name: string
}

/** Uma falha das quatro origens, na mesma forma (RN-ADM-98). Nunca o destinatário do envio. */
export interface OperationFailure {
  /** `operation:` · `delivery:` · `provider_event:` · `webhook_delivery:` e o id. */
  id: string
  area: OperationArea
  code: string
  title: string
  cause: string
  detail: string | null
  /** A última vez que aconteceu. */
  occurredAt: string
  firstOccurredAt: string
  /** Quantas vezes, somadas em janelas de 10 minutos (RN-ADM-95). */
  occurrences: number
  clinic: OperationClinic | null
  reference: string | null
}

export interface OperationFailuresMeta extends ListMeta {
  counts: Record<OperationAreaFilter, number>
}

export type AutentiqueEventState = 'applied' | 'pending' | 'ignored'
export type AutentiqueIgnoredReason =
  'unhandled_type' | 'no_target' | 'attempts_exhausted' | 'canceled_in_provider'

/** Um aviso da Autentique (RN-ADM-99), o "Histórico de eventos processados" do legado. */
export interface AutentiqueEvent {
  id: string
  receivedAt: string
  occurredAt: string | null
  type: string | null
  /** O identificador do documento na Autentique. */
  documentReference: string | null
  clinic: OperationClinic | null
  state: AutentiqueEventState
  ignoredReason: AutentiqueIgnoredReason | null
  attempts: number
  lastError: string | null
  protocolNumber: string | null
  /** A situação aqui do documento de menor posição no envio da mesma referência. */
  documentStatus: DocumentStatus | null
}

/** A tela pede tudo de novo a cada minuto, como a do legado (P-ADM-100). */
export const OPERATIONS_REFRESH_MS = 60_000
export const OPERATIONS_PER_PAGE = 25

export const operationKeys = {
  all: ['operations'] as const,
  overview: (period: OperationsPeriod) => ['operations', 'overview', period] as const,
  failures: (params: object) => ['operations', 'failures', params] as const,
  events: (params: object) => ['operations', 'autentique-events', params] as const,
}

export function useOperationsOverview(period: OperationsPeriod) {
  const api = useApi()
  return useQuery({
    queryKey: operationKeys.overview(period),
    queryFn: () => api.get<OperationsOverview>('/admin/operations', { period }),
    refetchInterval: OPERATIONS_REFRESH_MS,
    placeholderData: (previous) => previous,
  })
}

export function useOperationFailures(params: {
  period: OperationsPeriod
  area: OperationAreaFilter
  page: number
}) {
  const api = useApi()
  return useQuery({
    queryKey: operationKeys.failures(params),
    queryFn: () =>
      api.list<OperationFailure, OperationFailuresMeta>('/admin/operations/failures', {
        period: params.period,
        area: params.area,
        page: params.page,
        perPage: OPERATIONS_PER_PAGE,
      }),
    refetchInterval: OPERATIONS_REFRESH_MS,
    placeholderData: (previous) => previous,
  })
}

export function useAutentiqueEvents(params: { page: number }) {
  const api = useApi()
  return useQuery({
    queryKey: operationKeys.events(params),
    queryFn: () =>
      api.list<AutentiqueEvent>('/admin/operations/autentique-events', {
        page: params.page,
        perPage: OPERATIONS_PER_PAGE,
      }),
    refetchInterval: OPERATIONS_REFRESH_MS,
    placeholderData: (previous) => previous,
  })
}
